/**
 * CheckoutForm Component
 * Billing profile form + order placement — saves billing profile, then places credit purchase order
 */

import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation, Trans } from 'react-i18next';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { useBillingProfile, useUpdateBillingProfile } from '@/hooks/useBilling';
import { usePlaceOrder } from '@/hooks/useCredits';
import { useAuthStore } from '@/stores/authStore';
import { checkoutSchema, type CheckoutFormData } from '@/types/checkout';
import type { CreditPurchaseOrderResponse, UpdateBillingProfileRequest } from '@/types/profile';

interface CheckoutFormProps {
  quantity: number;
  pricePerCredit: number;
  currency: string;
  taxRate: number;
  onBack: () => void;
  onOrderComplete: (order: CreditPurchaseOrderResponse) => void;
  basePricePerCredit?: number;
  discountLabel?: string;
}

const inputClassName =
  'h-12 bg-white border-gray-200 focus:border-[var(--color-primary-start)] focus:ring-[var(--color-primary-start)]';
const selectTriggerClassName =
  'h-12 bg-white border-gray-200 focus:border-[var(--color-primary-start)] focus:ring-[var(--color-primary-start)]';
const labelClassName = 'text-[var(--color-charcoal)] font-medium';
const errorClassName = 'text-sm text-red-500';

/** ISO 3166-1 alpha-2 country codes with translation keys */
const COUNTRY_OPTIONS = [
  { code: 'DE', labelKey: 'deutschland' },
  { code: 'AT', labelKey: 'oesterreich' },
  { code: 'CH', labelKey: 'schweiz' },
] as const;

export function CheckoutForm({
  quantity,
  pricePerCredit,
  currency,
  taxRate,
  onBack,
  onOrderComplete,
  basePricePerCredit,
  discountLabel,
}: CheckoutFormProps) {
  const { t } = useTranslation();
  const [orderError, setOrderError] = useState<string | null>(null);

  const user = useAuthStore((s) => s.user);
  const { data: billingProfile, isLoading: profileLoading } = useBillingProfile();
  const updateBilling = useUpdateBillingProfile();
  const placeOrder = usePlaceOrder();

  const subtotal = quantity * pricePerCredit;
  const taxAmount = Math.round(subtotal * taxRate * 100) / 100;
  const total = Math.round((subtotal + taxAmount) * 100) / 100;
  const savingsAmount = basePricePerCredit
    ? Math.round((quantity * basePricePerCredit - subtotal) * 100) / 100
    : 0;

  const currencySymbol = currency === 'EUR' ? '€' : currency;
  const formatCurrency = (amount: number) =>
    amount.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const hasProfileAddress = !!(user?.street && user?.city && user?.postalCode);

  const {
    register,
    handleSubmit,
    reset,
    control,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CheckoutFormData>({
    resolver: zodResolver(checkoutSchema),
    defaultValues: {
      userOrganization: user?.organization ?? '',
      userEmail: user?.email ?? '',
      userStreet: user?.street ?? '',
      userHouseNumber: user?.houseNumber ?? '',
      userHouseNumberAddition: user?.houseNumberAddition ?? '',
      userCity: user?.city ?? '',
      userPostalCode: user?.postalCode ?? '',
      userCountry: user?.country ?? 'DE',
      billingFirstName: '',
      billingLastName: '',
      billingEmail: '',
      billingOrganization: '',
      billingSalutation: '',
      billingStreet: '',
      billingHouseNumber: '',
      billingHouseNumberAddition: '',
      billingCity: '',
      billingPostalCode: '',
      billingCountry: 'DE',
      vatNumber: '',
      useDifferentBillingAddress: false,
      acceptTerms: undefined as unknown as true,
    },
  });

  const useDifferentBillingAddress = watch('useDifferentBillingAddress');
  const acceptTerms = watch('acceptTerms');

  // Pre-fill from user profile and existing billing profile; detect differing address
  useEffect(() => {
    if (!user && !billingProfile) return;

    const hasBillingAddress = !!(billingProfile?.billingStreet && billingProfile?.billingCity);
    let differs = false;

    if (user && hasBillingAddress) {
      differs =
        billingProfile!.billingStreet !== (user.street ?? '') ||
        billingProfile!.billingHouseNumber !== (user.houseNumber ?? '') ||
        billingProfile!.billingCity !== (user.city ?? '') ||
        billingProfile!.billingPostalCode !== (user.postalCode ?? '') ||
        billingProfile!.billingCountry !== (user.country ?? '');
    }

    reset({
      userOrganization: user?.organization ?? '',
      userEmail: user?.email ?? '',
      userStreet: user?.street ?? '',
      userHouseNumber: user?.houseNumber ?? '',
      userHouseNumberAddition: user?.houseNumberAddition ?? '',
      userCity: user?.city ?? '',
      userPostalCode: user?.postalCode ?? '',
      userCountry: user?.country ?? 'DE',
      billingFirstName: billingProfile?.billingFirstName ?? '',
      billingLastName: billingProfile?.billingLastName ?? '',
      billingEmail: billingProfile?.billingEmail ?? '',
      billingOrganization: billingProfile?.billingOrganization ?? '',
      billingSalutation: billingProfile?.billingSalutation ?? '',
      billingStreet: billingProfile?.billingStreet ?? '',
      billingHouseNumber: billingProfile?.billingHouseNumber ?? '',
      billingHouseNumberAddition: billingProfile?.billingHouseNumberAddition ?? '',
      billingCity: billingProfile?.billingCity ?? '',
      billingPostalCode: billingProfile?.billingPostalCode ?? '',
      billingCountry: billingProfile?.billingCountry ?? 'DE',
      vatNumber: billingProfile?.vatNumber ?? '',
      useDifferentBillingAddress: differs,
      acceptTerms: undefined as unknown as true,
    });
  }, [billingProfile, reset, user, hasProfileAddress]);

  const onSubmit = async (data: CheckoutFormData) => {
    setOrderError(null);

    try {
      // 1. Save billing profile
      const payload: UpdateBillingProfileRequest = {
        billingFirstName: user?.firstName ?? '',
        billingLastName: user?.lastName ?? '',
        billingSalutation: user?.salutation || undefined,
        vatNumber: data.vatNumber || undefined,
      };

      if (data.useDifferentBillingAddress) {
        // Use billing address form field values
        payload.billingOrganization = data.billingOrganization;
        payload.billingEmail = data.billingEmail;
        payload.billingStreet = data.billingStreet;
        payload.billingHouseNumber = data.billingHouseNumber;
        payload.billingHouseNumberAddition = data.billingHouseNumberAddition || undefined;
        payload.billingCity = data.billingCity;
        payload.billingPostalCode = data.billingPostalCode;
        payload.billingCountry = data.billingCountry;
      }
      // When not using a different billing address, don't send address fields
      // so the existing billing profile address is preserved

      await updateBilling.mutateAsync(payload);

      // 2. Place the order
      const order = await placeOrder.mutateAsync(quantity);
      onOrderComplete(order);
    } catch {
      setOrderError(t('credits.checkout.orderError'));
    }
  };

  const isSubmitting = updateBilling.isPending || placeOrder.isPending;

  // Handle toggling the different billing address checkbox
  const handleDifferentAddressToggle = (checked: boolean) => {
    setValue('useDifferentBillingAddress', checked, { shouldValidate: true });

    if (checked && billingProfile) {
      // Pre-fill from billing profile if it has data
      if (billingProfile.billingStreet) {
        setValue('billingStreet', billingProfile.billingStreet ?? '');
        setValue('billingHouseNumber', billingProfile.billingHouseNumber ?? '');
        setValue('billingCity', billingProfile.billingCity ?? '');
        setValue('billingPostalCode', billingProfile.billingPostalCode ?? '');
        setValue('billingCountry', billingProfile.billingCountry ?? 'DE');
        setValue('billingOrganization', billingProfile.billingOrganization ?? '');
        setValue('billingEmail', billingProfile.billingEmail ?? '');
      }
    }
  };

  if (profileLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Order Summary */}
      <div className="rounded-[var(--radius-lg)] border border-[#e5e5ea] bg-[#f5f5f7] p-5">
        <h3 className="text-sm font-semibold text-[var(--color-charcoal)] mb-3">
          {t('credits.checkout.orderSummary')}
        </h3>
        <div className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-[var(--color-charcoal)]/60">
              {t('credits.checkout.quantity')}: {quantity} × {formatCurrency(pricePerCredit)} {currencySymbol}
            </span>
            <span className="font-medium text-[var(--color-charcoal)]">
              {formatCurrency(subtotal)} {currencySymbol}
            </span>
          </div>
          {savingsAmount > 0 && (
            <div className="flex justify-between text-emerald-600">
              <span>
                {t('credits.checkout.savings')}{discountLabel && ` (${discountLabel})`}
              </span>
              <span className="font-medium">
                -{formatCurrency(savingsAmount)} {currencySymbol}
              </span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-[var(--color-charcoal)]/60">
              {t('credits.checkout.vatLabel', { rate: String(Math.round(taxRate * 100)) })}
            </span>
            <span className="font-medium text-[var(--color-charcoal)]">
              {formatCurrency(taxAmount)} {currencySymbol}
            </span>
          </div>
          <div className="border-t border-[#e5e5ea] pt-2 flex justify-between">
            <span className="font-semibold text-[var(--color-charcoal)]">
              {t('credits.checkout.grossPrice')}
            </span>
            <span className="gradient-text font-bold text-lg">
              {formatCurrency(total)} {currencySymbol}
            </span>
          </div>
        </div>
        <p className="text-xs text-[var(--color-charcoal)]/60 mt-3">
          {t('credits.checkout.paymentNotice')}
        </p>
      </div>

      {/* Customer Data */}
      <div className="space-y-4">
        <h3 className="text-sm font-semibold text-[var(--color-charcoal)]">
          {t('credits.checkout.customerData')}
        </h3>

        {/* Salutation + Name (readonly) */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr_2fr] gap-4">
          <div className="space-y-2">
            <Label className={labelClassName}>{t('credits.checkout.salutationOptional')}</Label>
            <Input
              value={user?.salutation ?? ''}
              readOnly
              disabled
              className={cn(inputClassName, 'bg-gray-50 text-[var(--color-charcoal)]/70 cursor-not-allowed')}
            />
          </div>
          <div className="space-y-2">
            <Label className={labelClassName}>{t('credits.checkout.billingFirstName')}</Label>
            <Input
              value={user?.firstName ?? ''}
              readOnly
              disabled
              className={cn(inputClassName, 'bg-gray-50 text-[var(--color-charcoal)]/70 cursor-not-allowed')}
            />
          </div>
          <div className="space-y-2">
            <Label className={labelClassName}>{t('credits.checkout.billingLastName')}</Label>
            <Input
              value={user?.lastName ?? ''}
              readOnly
              disabled
              className={cn(inputClassName, 'bg-gray-50 text-[var(--color-charcoal)]/70 cursor-not-allowed')}
            />
          </div>
        </div>

        {/* Organization + Email (editable) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="checkout-userOrganization" className={labelClassName}>
              {t('credits.checkout.billingCompanyName')}
            </Label>
            <Input
              id="checkout-userOrganization"
              value={user?.organization ?? ''}
              readOnly
              disabled
              className={cn(inputClassName, 'bg-gray-50 text-[var(--color-charcoal)]/70 cursor-not-allowed')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="checkout-userEmail" className={labelClassName}>
              {t('credits.checkout.billingEmail')}
            </Label>
            <Input
              id="checkout-userEmail"
              type="email"
              value={user?.email ?? ''}
              readOnly
              disabled
              className={cn(inputClassName, 'bg-gray-50 text-[var(--color-charcoal)]/70 cursor-not-allowed')}
            />
          </div>
        </div>

        {/* Street + House Number + Addition (editable) */}
        <div className="grid grid-cols-1 sm:grid-cols-[3fr_1fr_1fr] gap-4">
          <div className="space-y-2">
            <Label htmlFor="checkout-userStreet" className={labelClassName}>
              {t('credits.checkout.street')}
            </Label>
            <Input
              id="checkout-userStreet"
              autoComplete="street-address"
              {...register('userStreet')}
              className={cn(inputClassName, errors.userStreet && 'border-red-500')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="checkout-userHouseNumber" className={labelClassName}>
              {t('credits.checkout.houseNumber')}
            </Label>
            <Input
              id="checkout-userHouseNumber"
              {...register('userHouseNumber')}
              className={cn(inputClassName, errors.userHouseNumber && 'border-red-500')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="checkout-userHouseNumberAddition" className={labelClassName}>
              {t('credits.checkout.houseNumberAddition')}
            </Label>
            <Input
              id="checkout-userHouseNumberAddition"
              {...register('userHouseNumberAddition')}
              className={inputClassName}
            />
          </div>
        </div>

        {/* Postal Code + City (editable) */}
        <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-4">
          <div className="space-y-2">
            <Label htmlFor="checkout-userPostalCode" className={labelClassName}>
              {t('credits.checkout.postalCode')}
            </Label>
            <Input
              id="checkout-userPostalCode"
              inputMode="numeric"
              autoComplete="postal-code"
              {...register('userPostalCode')}
              className={cn(inputClassName, errors.userPostalCode && 'border-red-500')}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="checkout-userCity" className={labelClassName}>
              {t('credits.checkout.city')}
            </Label>
            <Input
              id="checkout-userCity"
              autoComplete="address-level2"
              {...register('userCity')}
              className={cn(inputClassName, errors.userCity && 'border-red-500')}
            />
          </div>
        </div>

        {/* Country + VAT Number */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="checkout-userCountry" className={labelClassName}>
              {t('credits.checkout.country')}
            </Label>
            <Controller
              name="userCountry"
              control={control}
              render={({ field }) => (
                <Select value={field.value} onValueChange={field.onChange}>
                  <SelectTrigger
                    id="checkout-userCountry"
                    className={cn(selectTriggerClassName, errors.userCountry && 'border-red-500')}
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {COUNTRY_OPTIONS.map(({ code, labelKey }) => (
                      <SelectItem key={code} value={code}>
                        {t(`credits.checkout.countryOptions.${labelKey}`)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
            {errors.userCountry && (
              <p className={errorClassName}>
                {t('validation.fieldRequired', { field: t('credits.checkout.country') })}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="checkout-vatNumber" className={labelClassName}>
              {t('credits.checkout.vatNumberOptional')}
            </Label>
            <Input
              id="checkout-vatNumber"
              {...register('vatNumber')}
              className={inputClassName}
              placeholder="DE123456789"
            />
          </div>
        </div>
      </div>

      {/* Abweichende Lieferstelle (Different Delivery Address) */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Controller
            name="useDifferentBillingAddress"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="differentDeliveryAddress"
                checked={field.value}
                onCheckedChange={(checked) => handleDifferentAddressToggle(checked === true)}
              />
            )}
          />
          <Label
            htmlFor="differentDeliveryAddress"
            className="text-sm font-semibold text-[var(--color-charcoal)] cursor-pointer select-none"
          >
            {t('credits.checkout.differentDeliveryAddress')}
          </Label>
        </div>

        {/* No profile address warning */}
        {!hasProfileAddress && !useDifferentBillingAddress && (
          <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {t('credits.checkout.noProfileAddress')}
          </p>
        )}

        {/* Billing address fields — shown when checkbox is checked */}
        <div
          className={cn(
            'grid transition-all duration-300 ease-in-out',
            useDifferentBillingAddress
              ? 'grid-rows-[1fr] opacity-100'
              : 'grid-rows-[0fr] opacity-0',
          )}
        >
          <div className="overflow-hidden">
            <div className="space-y-4 pt-1 pl-4 border-l-2 border-[var(--color-primary-start)]/20 ml-1">
              {/* billingOrganization + billingEmail */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="checkout-billing-companyName" className={labelClassName}>
                    {t('credits.checkout.billingCompanyName')}
                  </Label>
                  <Input
                    id="checkout-billing-companyName"
                    {...register('billingOrganization')}
                    className={cn(inputClassName, errors.billingOrganization && 'border-red-500')}
                  />
                  {errors.billingOrganization && (
                    <p className={errorClassName}>
                      {t('validation.fieldRequired', { field: t('credits.checkout.billingCompanyName') })}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="checkout-billing-email" className={labelClassName}>
                    {t('credits.checkout.billingEmail')}
                  </Label>
                  <Input
                    id="checkout-billing-email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    {...register('billingEmail')}
                    className={cn(inputClassName, errors.billingEmail && 'border-red-500')}
                  />
                  {errors.billingEmail && (
                    <p className={errorClassName}>
                      {errors.billingEmail.type === 'too_small'
                        ? t('validation.fieldRequired', { field: t('credits.checkout.billingEmail') })
                        : t('validation.invalidEmail')}
                    </p>
                  )}
                </div>
              </div>

              {/* billingStreet + billingHouseNumber + billingHouseNumberAddition */}
              <div className="grid grid-cols-1 sm:grid-cols-[3fr_1fr_1fr] gap-4">
                <div className="space-y-2">
                  <Label htmlFor="checkout-billingStreet" className={labelClassName}>
                    {t('credits.checkout.street')}
                  </Label>
                  <Input
                    id="checkout-billingStreet"
                    {...register('billingStreet')}
                    className={cn(inputClassName, errors.billingStreet && 'border-red-500')}
                  />
                  {errors.billingStreet && (
                    <p className={errorClassName}>
                      {t('validation.fieldRequired', { field: t('credits.checkout.street') })}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="checkout-billingHouseNumber" className={labelClassName}>
                    {t('credits.checkout.houseNumber')}
                  </Label>
                  <Input
                    id="checkout-billingHouseNumber"
                    {...register('billingHouseNumber')}
                    className={cn(inputClassName, errors.billingHouseNumber && 'border-red-500')}
                  />
                  {errors.billingHouseNumber && (
                    <p className={errorClassName}>
                      {t('validation.fieldRequired', { field: t('credits.checkout.houseNumber') })}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="checkout-billingHouseNumberAddition" className={labelClassName}>
                    {t('credits.checkout.houseNumberAddition')}
                  </Label>
                  <Input
                    id="checkout-billingHouseNumberAddition"
                    {...register('billingHouseNumberAddition')}
                    className={inputClassName}
                  />
                </div>
              </div>

              {/* billingPostalCode + billingCity */}
              <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-4">
                <div className="space-y-2">
                  <Label htmlFor="checkout-billingPostalCode" className={labelClassName}>
                    {t('credits.checkout.postalCode')}
                  </Label>
                  <Input
                    id="checkout-billingPostalCode"
                    inputMode="numeric"
                    autoComplete="postal-code"
                    {...register('billingPostalCode')}
                    className={cn(inputClassName, errors.billingPostalCode && 'border-red-500')}
                  />
                  {errors.billingPostalCode && (
                    <p className={errorClassName}>
                      {t('validation.fieldRequired', { field: t('credits.checkout.postalCode') })}
                    </p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="checkout-billingCity" className={labelClassName}>
                    {t('credits.checkout.city')}
                  </Label>
                  <Input
                    id="checkout-billingCity"
                    {...register('billingCity')}
                    className={cn(inputClassName, errors.billingCity && 'border-red-500')}
                  />
                  {errors.billingCity && (
                    <p className={errorClassName}>
                      {t('validation.fieldRequired', { field: t('credits.checkout.city') })}
                    </p>
                  )}
                </div>
              </div>

              {/* billingCountry */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="checkout-billingCountry" className={labelClassName}>
                    {t('credits.checkout.country')}
                  </Label>
                  <Controller
                    name="billingCountry"
                    control={control}
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger
                          id="checkout-billingCountry"
                          className={cn(selectTriggerClassName, errors.billingCountry && 'border-red-500')}
                        >
                          <SelectValue placeholder={t('credits.checkout.country')} />
                        </SelectTrigger>
                        <SelectContent>
                          {COUNTRY_OPTIONS.map(({ code, labelKey }) => (
                            <SelectItem key={code} value={code}>
                              {t(`credits.checkout.countryOptions.${labelKey}`)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                  {errors.billingCountry && (
                    <p className={errorClassName}>
                      {t('validation.fieldRequired', { field: t('credits.checkout.country') })}
                    </p>
                  )}
                </div>
                <div />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terms & Conditions */}
      <div className="space-y-2">
        <div className="flex items-start gap-2">
          <Controller
            name="acceptTerms"
            control={control}
            render={({ field }) => (
              <Checkbox
                id="acceptTerms"
                checked={field.value === true}
                onCheckedChange={(checked) => field.onChange(checked === true ? true : undefined)}
                className="mt-0.5"
              />
            )}
          />
          <Label
            htmlFor="acceptTerms"
            className="text-sm text-[var(--color-charcoal)] cursor-pointer select-none leading-relaxed"
          >
            <Trans
              i18nKey="credits.checkout.acceptTerms"
              components={{
                agbLink: (
                  <a
                    href="https://eclou.de/agb"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-primary-start)] underline hover:opacity-80"
                  />
                ),
                privacyLink: (
                  <a
                    href="https://eclou.de/datenschutz"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-[var(--color-primary-start)] underline hover:opacity-80"
                  />
                ),
              }}
            />
          </Label>
        </div>
        {errors.acceptTerms && (
          <p className={errorClassName}>
            {t('credits.checkout.acceptTermsRequired')}
          </p>
        )}
      </div>

      {/* Error message */}
      {orderError && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {orderError}
        </div>
      )}

      {/* Footer */}
      <div className="flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-3 pt-2">
        <Button
          type="button"
          variant="ghost"
          onClick={onBack}
          disabled={isSubmitting}
          className="w-full sm:w-auto text-[var(--color-charcoal)]/70 hover:text-[var(--color-charcoal)]"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('credits.checkout.back')}
        </Button>
        <Button
          type="submit"
          className="btn-primary w-full sm:w-auto px-8 py-3 rounded-full text-base"
          disabled={isSubmitting || acceptTerms !== true}
        >
          {isSubmitting && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
          {t('credits.checkout.placeOrder')}
        </Button>
      </div>
    </form>
  );
}
